/**
 * Vue binding for a local TEA loop: one reactive `model`, `dispatch(msg)`.
 *
 * Put pure `update` in a sibling `*.update.ts`. Prefer this over one-off
 * `ref(false)` flags. See `.cursor/rules/tea-pure-update.mdc`.
 */
import { shallowRef, type ShallowRef } from "vue";
import { isNothing, nothing, type Maybe } from "../maybe";
import type { RunCmd, UpdateFn } from "./dispatch";
import { runCmds } from "./runCmds";

export type TeaLoop<Model, Msg> = {
  /** Elm-style model. Replaced wholesale on each Msg (shallowRef). */
  readonly model: ShallowRef<Model>;
  readonly dispatch: (msg: Msg) => Promise<void>;
};

export type UseTeaOptions<Cmd> = {
  /** Cmd interpreter. Required if `update` ever returns Cmds. */
  readonly runCmd: Maybe<RunCmd<Cmd>>;
};

/**
 * @param init - Initial model (even trivial UI: `{ open: false }`, not `ref(false)`).
 * @param update - Pure `(model, msg) → [model, Cmd[]]` from `*.update.ts`.
 * @param options - Pass `{ runCmd: just(fn) }` when the loop has effects; default is nothing.
 */
export function useTea<Model, Msg, Cmd = never>(
  init: Model,
  update: UpdateFn<Model, Msg, Cmd>,
  options: UseTeaOptions<Cmd> = { runCmd: nothing },
): TeaLoop<Model, Msg> {
  const model: ShallowRef<Model> = shallowRef(init);
  const runCmdM = options.runCmd;

  async function dispatchMsg(msg: Msg): Promise<void> {
    const [next, cmds] = update(model.value, msg);
    // Apply model before effects (Elm order) so Cmds see the new state.
    model.value = next;
    if (cmds.length === 0) return;
    if (isNothing(runCmdM)) {
      throw new Error("useTea: update returned Cmds but runCmd was nothing");
    }
    await runCmds(cmds, runCmdM.value);
  }

  return { model, dispatch: dispatchMsg };
}
